'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';

// Trecho(s) de um video do YouTube tocando mudo e em loop, como "cinematic".
// Usa o player oficial (IFrame API) em vez de baixar/hospedar o arquivo: o
// video e de terceiros, entao ele continua sendo servido pelo YouTube.
// `segmentos` e uma lista de [inicioSeg, fimSeg]; ao chegar no fim de um, pula
// para o inicio do proximo e, no ultimo, volta ao primeiro. O parametro `end`
// do embed nao serve aqui: ele encerra o video (tela final) em vez de pular.

type Segmento = [number, number];

type YTPlayer = {
  destroy: () => void;
  getCurrentTime: () => number;
  seekTo: (s: number, allowSeekAhead: boolean) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  unloadModule?: (nome: string) => void;
  mute?: () => void;
  isMuted?: () => boolean;
  setVolume?: (volume: number) => void;
  getVolume?: () => number;
};

type YTApi = {
  Player: new (el: HTMLElement, opts: Record<string, unknown>) => YTPlayer;
  PlayerState: { PLAYING: number };
};

declare global {
  interface Window {
    YT?: YTApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<YTApi> | null = null;

/** Carrega o script da IFrame API uma unica vez, mesmo com varios clipes na pagina. */
function carregarApi(): Promise<YTApi> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise<YTApi>((resolve, reject) => {
    const anterior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      anterior?.();
      if (window.YT?.Player) resolve(window.YT);
      else reject(new Error('YT indisponivel'));
    };

    // Tres cuidados, todos por um bug visto em producao (o script sumia e o
    // video de fundo nunca comecava):
    // 1) anexa ao <body>, nao ao <head>: o React/Next reconcilia o <head> na
    //    hidratacao e apaga <script> inserido por fora antes dele executar;
    // 2) espera o `load` da pagina, para nao disputar com a hidratacao;
    // 3) se nao chegou, reinjeta (ate 3 vezes) e so entao desiste, caindo no
    //    fallback - em vez de ficar pendente para sempre.
    let tentativas = 0;
    const injetar = () => {
      if (window.YT?.Player) {
        resolve(window.YT);
        return;
      }
      if (tentativas >= 3) {
        reject(new Error('IFrame API nao carregou'));
        return;
      }
      tentativas++;
      document.getElementById('yt-iframe-api')?.remove();
      const s = document.createElement('script');
      s.id = 'yt-iframe-api';
      s.src = 'https://www.youtube.com/iframe_api';
      s.async = true;
      document.body.appendChild(s);
      setTimeout(injetar, 7000);
    };
    const comecar = () => setTimeout(injetar, 300);
    if (document.readyState === 'complete') comecar();
    else window.addEventListener('load', comecar, { once: true });
  });
  apiPromise.catch(() => {
    apiPromise = null;
  });
  return apiPromise;
}

export default function ClipeYouTube({
  videoId,
  segmentos,
  label,
  linkLabel,
  fundo = false,
}: {
  videoId: string;
  segmentos: Segmento[];
  /** Descricao acessivel do clipe (o video e decorativo, sem som). */
  label: string;
  /** Texto do link de fallback "assistir no YouTube". */
  linkLabel: string;
  /**
   * Modo fundo: o clipe preenche (cover) o pai posicionado, sem cantos nem
   * sombra, sem receber cliques e escondido de leitores de tela. O chamador
   * cuida do veu de cor e do credito.
   */
  fundo?: boolean;
}) {
  const caixaRef = useRef<HTMLDivElement>(null);
  const alvoRef = useRef<HTMLDivElement>(null);
  const [fallback, setFallback] = useState(false);

  const urlYouTube = `https://www.youtube.com/watch?v=${videoId}&t=${segmentos[0][0]}s`;

  useEffect(() => {
    const caixa = caixaRef.current;
    const alvo = alvoRef.current;
    if (!caixa || !alvo) return;

    // Quem pediu menos movimento nao recebe video em autoplay: fica so o link.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setFallback(true);
      return;
    }

    // O player troca o elemento que recebe por um <iframe>. Se esse elemento
    // fosse um no gerenciado pelo React, o React tentaria remove-lo depois (ex.:
    // ao cair no fallback) e o removeChild quebraria. Por isso o alvo e criado
    // aqui, dentro do wrapper, e o React nunca o enxerga.
    const miolo = document.createElement('div');
    miolo.style.width = '100%';
    miolo.style.height = '100%';
    alvo.appendChild(miolo);

    // Clipe decorativo: nunca com som nem legenda. O embed ja nasce com mute=1 e
    // cc_load_policy=0, mas isso nao vale se o visitante tem legenda sempre ligada
    // na conta do YouTube (ou traducao automatica) - por isso reforca aqui.
    const silenciar = (p: YTPlayer) => {
      p.mute?.();
      p.setVolume?.(0);
      p.unloadModule?.('captions');
      p.unloadModule?.('cc');
    };

    let player: YTPlayer | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let indice = 0;
    let visivel = false;
    let iniciou = false;
    let morto = false;

    const parar = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    // Confere o tempo a cada 200ms; so roda enquanto o video esta tocando.
    const vigiar = () => {
      parar();
      timer = setInterval(() => {
        if (!player) return;
        // Clipe decorativo: se algo religou o som (preferencia de volume salva
        // pelo YouTube, gesto do usuario, outro navegador), silencia de novo.
        if (player.isMuted?.() === false || (player.getVolume?.() ?? 0) > 0) {
          player.mute?.();
          player.setVolume?.(0);
        }
        const [, fim] = segmentos[indice];
        if (player.getCurrentTime() >= fim - 0.15) {
          indice = (indice + 1) % segmentos.length;
          player.seekTo(segmentos[indice][0], true);
        }
      }, 200);
    };

    const iniciar = () => {
      if (iniciou || morto) return;
      iniciou = true;
      carregarApi()
        .then((YT) => {
          if (morto) return;
          player = new YT.Player(miolo, {
            // youtube-nocookie: sem cookies de rastreio ate o visitante interagir
            host: 'https://www.youtube-nocookie.com',
            videoId,
            playerVars: {
              autoplay: 1,
              mute: 1,
              controls: 0,
              disablekb: 1,
              fs: 0,
              iv_load_policy: 3,
              cc_load_policy: 0,
              playsinline: 1,
              rel: 0,
              start: segmentos[0][0],
              origin: window.location.origin,
            },
            events: {
              onReady: (e: { target: YTPlayer }) => {
                silenciar(e.target);
                e.target.seekTo(segmentos[0][0], true);
                if (visivel) e.target.playVideo();
                else e.target.pauseVideo();
              },
              onStateChange: (e: { data: number; target: YTPlayer }) => {
                if (e.data === YT.PlayerState.PLAYING) {
                  // reaplica: o YouTube pode religar legenda/som ao retomar ou apos um seek
                  silenciar(e.target);
                  vigiar();
                } else parar();
              },
              // dispara quando o modulo de legendas termina de carregar - e a hora
              // certa de descarregar, ja que antes disso o unloadModule nao pega
              onApiChange: (e: { target: YTPlayer }) => silenciar(e.target),
              onError: () => setFallback(true),
            },
          });
        })
        .catch(() => setFallback(true));
    };

    // So carrega o player (e o script do YouTube) quando o clipe esta perto da
    // tela, e pausa fora dela: poupa banda e nao chama o YouTube a toa.
    const obs = new IntersectionObserver(
      ([entrada]) => {
        visivel = entrada.isIntersecting;
        if (visivel) {
          iniciar();
          player?.playVideo();
        } else {
          player?.pauseVideo();
        }
      },
      { rootMargin: '200px 0px' }
    );
    obs.observe(caixa);

    return () => {
      morto = true;
      obs.disconnect();
      parar();
      player?.destroy();
      miolo.remove();
    };
    // segmentos e uma constante do chamador; videoId identifica o clipe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Modo fundo: o iframe 16:9 e dimensionado em unidades de container (cq) para
  // cobrir o pai inteiro, cortando o excesso, como um object-fit: cover.
  const estiloCaixa: CSSProperties = fundo
    ? { containerType: 'size', background: '#050d1f' }
    : {
        aspectRatio: '16 / 9',
        borderRadius: '16px',
        background: '#050d1f',
        boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      };
  const estiloAlvo: CSSProperties = fundo
    ? {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 'max(100cqw, calc(100cqh * 16 / 9))',
        height: 'max(100cqh, calc(100cqw * 9 / 16))',
        // scale(1.25) corta ~12% de cada borda: a faixa de titulo/avatar que o
        // YouTube mostra no topo do player nos primeiros segundos cai fora do
        // hero em vez de aparecer atras do texto.
        transform: 'translate(-50%, -50%) scale(1.25)',
        display: fallback ? 'none' : 'block',
      }
    : { display: fallback ? 'none' : 'block' };

  return (
    <div
      ref={caixaRef}
      {...(fundo ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
      className={fundo ? 'absolute inset-0 overflow-hidden pointer-events-none' : 'relative overflow-hidden'}
      style={estiloCaixa}
    >
      {/* Sempre renderizado: o player e injetado la dentro por useEffect */}
      <div
        ref={alvoRef}
        className={
          fundo
            ? '[&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0'
            : 'absolute inset-0 [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0'
        }
        style={estiloAlvo}
      />
      {fallback && fundo && (
        // Sem player (menos movimento / erro): so a miniatura como fundo estatico
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(https://i.ytimg.com/vi/${videoId}/hqdefault.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      {fallback && !fundo && (
        <a
          href={urlYouTube}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute inset-0 flex items-center justify-center text-white"
          style={{
            backgroundImage: `url(https://i.ytimg.com/vi/${videoId}/hqdefault.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <span
            style={{
              background: 'rgba(5,13,31,0.75)',
              borderRadius: '999px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            ▶ {linkLabel}
          </span>
        </a>
      )}
    </div>
  );
}
